// First-touch attribution — the shared, isomorphic half.
//
// Until now no account in the database knew where it came from. UTM was read in
// the browser and only ever landed on public.waitlist, and the landing CTA stopped
// feeding the waitlist when it started pointing at signup — so "where did these
// people come from" had no answer in SQL, and acquisition source could never be
// joined to what someone actually did with the product.
//
// This module is the single definition of an attribution record's shape, so the
// browser half (lib/utm.ts) and the write surface (app/api/attribution/route.ts)
// agree on it and neither can quietly widen it. Both sides sanitise through
// here: the client so it never stores something it shouldn't, the server because
// a payload arriving from a browser is never trusted.
//
// Two laws live in this file rather than at the call sites, because call sites
// forget:
//
//   · NO COOKIES. Nothing here reads or writes one. The browser half keeps
//     first-touch in localStorage, the same posture as PostHog's cookieless
//     persistence — /privacy promises no tracking cookies and this is not the
//     place to spend that promise.
//   · NO QUERY STRINGS. A URL on this site can carry a PKCE `?code=`, a
//     magic-link `token_hash`, or an email — which is exactly why
//     instrumentation-client.ts scrubs PostHog's URL properties. Rather than
//     denylist the dangerous keys, attribution keeps origin + path and drops
//     everything from the first `?` or `#` onward. Campaign identity already
//     lives in the utm_* fields; the rest of the query is nobody's business.

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
export type UtmKey = (typeof UTM_KEYS)[number];

/** One visitor's first touch — where they came from, before they were anyone. */
export interface FirstTouch extends Partial<Record<UtmKey, string>> {
  /** The external page that sent them, origin + path, never a query string. */
  referrer?: string;
  /** Host only. The field you actually GROUP BY when reading the numbers. */
  referrer_domain?: string;
  /** The path they landed on here — again, path only. */
  landing_path?: string;
  /** ISO timestamp of that first landing, which can predate the account by days. */
  first_seen_at?: string;
  /**
   * WHICH DOOR THEY CAME THROUGH, recorded at the moment the account is made.
   *
   * `landing_path` cannot answer this and never could: the beacon fires on the
   * first page a browser loads, which for most people is /dashboard after an
   * auth round trip — 87 of the first 110 rows say exactly that, and not one
   * has ever said /oauth/consent. So the two doors say so themselves instead
   * of leaving it to be inferred later.
   */
  arrival?: Arrival;
  /**
   * The tool that sent them — the client_name the consent page was showing, or
   * 'npx innernet'. It is the one fact this module's no-query-strings law
   * would otherwise destroy, so the door hands it over explicitly.
   */
  arrival_client?: string;
}

/**
 * The three doors. Anything else is dropped rather than stored, because a
 * free-text arrival is a column that means something different every quarter.
 */
export const ARRIVALS = ["connect_first", "cli_first", "web_first"] as const;
export type Arrival = (typeof ARRIVALS)[number];

// Campaign values are short by convention; a long one is either a mistake or an
// attempt to use us as storage. Referrers are allowed a little more room because
// a real article URL can be long.
const MAX_TAG = 180;
const MAX_URL = 500;

const text = (v: unknown, max: number): string | undefined => {
  if (typeof v !== "string") return undefined;
  const s = v.trim().slice(0, max);
  return s || undefined;
};

/**
 * Origin + path of a URL, with the query and hash removed. Relative inputs (a
 * pathname) are handled too, so this is safe on both `document.referrer` and
 * `window.location.pathname`. Anything unparseable is cut at the first `?`/`#`
 * as a fallback — the strip must never fail open.
 */
export function stripQuery(raw: unknown, max = MAX_URL): string | undefined {
  const s = text(raw, max * 2);
  if (!s) return undefined;
  try {
    // The base only matters for relative inputs and is discarded for those, so
    // it can be any absolute URL.
    const u = new URL(s, "https://innernet.live");
    // An app referrer (`android-app://com.reddit.frontpage/` — every tap out of
    // the Reddit or X app on Android) has an OPAQUE origin, which the URL
    // parser spells "null" — so every app arrival was stored with the referrer
    // "null/" (3 rows by 2026-09-17). Build it from protocol + host instead.
    const origin = u.origin === "null" && u.host ? `${u.protocol}//${u.host}` : u.origin;
    const bare = s.startsWith("/") ? u.pathname : `${origin}${u.pathname}`;
    return text(bare, max);
  } catch {
    return text(s.split(/[?#]/)[0], max);
  }
}

/** The host that sent them — the coarse grain most acquisition questions want. */
export function domainOf(raw: unknown): string | undefined {
  const s = text(raw, MAX_URL * 2);
  if (!s) return undefined;
  try {
    return text(new URL(s).hostname.replace(/^www\./, ""), MAX_TAG);
  } catch {
    return undefined;
  }
}

// ── in-app browsers · the app IS the referrer ───────────────────────────────
//
// A link tapped inside Instagram, Facebook, TikTok or LinkedIn opens in that
// app's own browser, and on iOS that browser sends NO referrer. Measured
// 2026-10-03, the morning a creator's reel went up: 14 people reached the site
// inside Instagram's browser and every one of them read "direct", while the
// user-agent said "Instagram 4xx" plainly the whole time. When the page that
// sent them is blank, the app they are standing in is the honest answer, and
// it is read from the user-agent, which both halves have (the browser at
// landing, the server on the write).

const IN_APP: [RegExp, string][] = [
  [/\binstagram\b/i, "instagram.com"],
  [/\bbarcelona\b/i, "threads.net"], // the Threads app names itself by its codename
  [/\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bFBIOS\b/, "facebook.com"],
  [/\bmusical_ly\b|\bBytedanceWebview\b|\bTikTok\b/i, "tiktok.com"],
  [/\bLinkedInApp\b/i, "linkedin.com"],
  [/\bSnapchat\b/i, "snapchat.com"],
];

/** The app whose built-in browser this is, as a host, or undefined for a real browser. */
export function inAppHost(ua: string | null | undefined): string | undefined {
  const s = String(ua ?? "");
  if (!s) return undefined;
  for (const [re, host] of IN_APP) if (re.test(s)) return host;
  return undefined;
}

/**
 * Fill a record's blank referrer from the in-app browser it was made in. Only
 * ever fills a blank (or our own host, or the google sign-in hop): a real
 * referrer, or a campaign, is the better answer and is left alone.
 */
export function withInAppReferrer(
  ft: FirstTouch,
  ua: string | null | undefined,
  ownHosts: readonly string[] = ["innernet.live"],
): FirstTouch {
  if (UTM_KEYS.some((k) => ft[k])) return ft;
  const host = ft.referrer_domain?.toLowerCase();
  if (host && !isOwnHost(host, ownHosts) && !AUTH_RETURN.has(host)) return ft;
  const app = inAppHost(ua);
  if (!app) return ft;
  // spelled `in-app://` so the record says what it rests on: the app was read
  // off the user-agent, no page named it (firstTouchStrength reads this)
  return { ...ft, referrer: `${IN_APP_SCHEME}${app}/`, referrer_domain: app };
}

/** The referrer scheme of a first touch inferred from the in-app browser. */
export const IN_APP_SCHEME = "in-app://";

/**
 * Narrow anything claiming to be a first touch into one. Applied on the client
 * before storing and again on the server before writing — the second pass is the
 * one that matters, since the first can be edited by whoever owns the browser.
 */
export function sanitizeFirstTouch(raw: unknown): FirstTouch {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const out: FirstTouch = {};

  for (const k of UTM_KEYS) {
    const v = text(src[k], MAX_TAG);
    if (v) out[k] = v;
  }

  const referrer = stripQuery(src.referrer);
  if (referrer) {
    out.referrer = referrer;
    // Prefer a domain the client already derived, but re-derive rather than
    // trust it — it is the field the reporting groups by.
    out.referrer_domain = domainOf(referrer) ?? text(src.referrer_domain, MAX_TAG);
  }

  const landing = stripQuery(src.landing_path, MAX_TAG);
  if (landing) out.landing_path = landing;

  // A clock we don't control: accept it only if it parses and isn't in the
  // future (a skewed device would otherwise sort ahead of every real row).
  const seen = text(src.first_seen_at, 40);
  if (seen) {
    const t = Date.parse(seen);
    if (Number.isFinite(t) && t <= Date.now() + 60_000) out.first_seen_at = new Date(t).toISOString();
  }

  // The door, allow-listed. A browser can put anything in localStorage, and
  // this value goes on to split every acquisition number the console reports.
  const arrival = text(src.arrival, 20);
  if (arrival && (ARRIVALS as readonly string[]).includes(arrival)) out.arrival = arrival as Arrival;
  // The client name is supplied by whoever registered the OAuth client, so it
  // is neither ours nor the person's — kept short, never rendered as html.
  const client = text(src.arrival_client, MAX_TAG);
  if (client) out.arrival_client = client;

  return out;
}

/**
 * True when the record says something beyond "someone arrived". A first touch
 * with no campaign and no referrer is still worth recording — "direct" is an
 * answer to the question — so this is for reporting, not for gating the write.
 */
export function hasSignal(ft: FirstTouch): boolean {
  return Boolean(ft.referrer_domain || UTM_KEYS.some((k) => ft[k]));
}

// ── strength · which of two records deserves to be the account's ─────────────
//
// The signup crosses TWO ORIGINS (innernet.live, then the dashboard host), and
// first touch lives in localStorage, which is per-origin. The dashboard origin
// therefore recomputes a first touch of its own on arrival — referrer: our own
// apex, landing_path: /dashboard — and races the real record to the write-once
// row. Measured before this: 87 of the first 110 rows said '/dashboard'.
//
// So a record can now be WEAK: it carries no campaign, its referrer is us (or
// nothing), and it landed on an app path or nowhere. A weak row is the origin
// hop describing itself, not the person's arrival, and the server may let a
// STRONG record (a real referrer, a campaign, or a public landing page) replace
// it. Strong never yields to anything — first touch still wins.

const APP_LANDING = /^\/(dashboard|projects|account|me|connectors|admin|ours\/|auth\/|oauth\/|api\/)/;

/** Hosts a browser comes BACK from during sign-in. Never a source. */
const AUTH_RETURN = new Set(["accounts.google.com"]);

/** Hosts that are US — a referrer from here is the site sending itself. */
export function isOwnHost(host: string | null | undefined, ownHosts: readonly string[]): boolean {
  const h = (host ?? "").toLowerCase().replace(/^www\./, "");
  if (!h) return false;
  return ownHosts.some((o) => {
    const own = o.toLowerCase().replace(/^www\./, "");
    return h === own || h.endsWith(`.${own}`);
  });
}

export type FirstTouchStrength = "strong" | "weak";

export function firstTouchStrength(
  ft: Pick<FirstTouch, "utm_source" | "utm_campaign" | "utm_medium" | "referrer_domain" | "landing_path"> & {
    referrer?: string | null;
  },
  ownHosts: readonly string[],
): FirstTouchStrength {
  if (ft.utm_source || ft.utm_campaign || ft.utm_medium) return "strong";
  // the in-app browser named on the sign-in hop (an app landing) is a good
  // guess and no more: it must never outrank the landing's own record, which
  // may carry the creator's link
  const landing0 = ft.landing_path ?? "";
  if (ft.referrer?.startsWith(IN_APP_SCHEME) && (!landing0 || APP_LANDING.test(landing0))) return "weak";
  // The google sign-in page is the OAuth round trip, not where anybody came
  // from. Counted as a real referrer, the dashboard hop's record (referrer
  // accounts.google.com, landing /dashboard) read STRONG — so the apex record
  // carrying the actual Reddit or X referrer could never replace it. ~70 of the
  // first 143 rows are exactly that hop.
  if (ft.referrer_domain && !isOwnHost(ft.referrer_domain, ownHosts) && !AUTH_RETURN.has(ft.referrer_domain.toLowerCase()))
    return "strong";
  const landing = ft.landing_path ?? "";
  if (landing && !APP_LANDING.test(landing)) return "strong";
  return "weak";
}

// ── channel · the name a person actually searches for ────────────────────────
//
// `referrer_domain` is the truth, and it is useless to read. The Reddit app
// opens links with `android-app://com.reddit.frontpage/` as the referrer, the X
// app with `com.twitter.android`, Instagram through `l.instagram.com`, X on the
// web through `t.co`. Measured 2026-09-17 over 30 days of pageviews: 65 of the 95
// browsers Reddit sent carried `com.reddit.frontpage`, and the single biggest
// referrer on the whole site was `com.twitter.android` (475 browsers). Neither
// PostHog's channel classifier nor a person scanning the console reads either
// of those as "reddit" or "x", so the work of posting there looked like nothing.
//
// This folds every spelling of a place into one lowercase name. A campaign tag
// wins over the referrer, because a person chose it; an unknown host is kept as
// the host rather than guessed at.

const CHANNELS: [RegExp, string][] = [
  [/(^|\.)reddit\.com$|^com\.reddit\.|^redd\.it$/, "reddit"],
  [/^(t\.co|x\.com|twitter\.com|mobile\.twitter\.com)$|^com\.twitter\.|(^|\.)x\.com$/, "x"],
  [/(^|\.)instagram\.com$|^com\.instagram\./, "instagram"],
  [/(^|\.)facebook\.com$|^com\.facebook\.|^fb\.me$/, "facebook"],
  [/(^|\.)threads\.net$|^com\.instagram\.barcelona$/, "threads"],
  [/(^|\.)linkedin\.com$|^lnkd\.in$|^com\.linkedin\./, "linkedin"],
  [/(^|\.)youtube\.com$|^youtu\.be$|^com\.google\.android\.youtube$/, "youtube"],
  [/^news\.ycombinator\.com$/, "hacker news"],
  [/(^|\.)producthunt\.com$/, "product hunt"],
  [/(^|\.)github\.com$/, "github"],
  [/(^|\.)discord(app)?\.com$|^discord\.gg$/, "discord"],
  [/(^|\.)tiktok\.com$|^com\.zhiliaoapp\.musically$/, "tiktok"],
  [/(^|\.)snapchat\.com$/, "snapchat"],
  [/^(chatgpt\.com|chat\.openai\.com)$/, "chatgpt"],
  [/^claude\.ai$/, "claude"],
  [/(^|\.)perplexity\.ai$/, "perplexity"],
  [/^mail\.google\.com$|^com\.google\.android\.gm$/, "gmail"],
  // the google LOGIN page is the auth round trip, never a source
  [/^(www\.)?google\.[a-z.]+$|^com\.google\.android\.googlequicksearchbox$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
]

/** What people type in a utm_source, folded to the same names. */
const UTM_ALIASES: Record<string, string> = {
  ig: "instagram",
  insta: "instagram",
  fb: "facebook",
  twitter: "x",
  tw: "x",
  li: "linkedin",
  yt: "youtube",
  hn: "hacker news",
  ph: "product hunt",
  r: "reddit",
}

/** One lowercase place name for a host (or an android package name). */
export function channelOfHost(host: string | null | undefined): string | undefined {
  const h = (host ?? "").trim().toLowerCase().replace(/^www\./, "")
  if (!h) return undefined
  for (const [re, name] of CHANNELS) if (re.test(h)) return name
  return h
}

/**
 * Where a person came from, as the one word the console and PostHog group by.
 * `ownHosts` keeps the site from crediting itself (and the google sign-in
 * page from being mistaken for google search): either reads as "direct".
 */
export function channelOf(
  ft: Pick<FirstTouch, "utm_source" | "referrer_domain" | "landing_path">,
  ownHosts: readonly string[] = ["innernet.live"],
): string {
  const utm = ft.utm_source?.trim().toLowerCase()
  if (utm) return UTM_ALIASES[utm] ?? channelOfHost(utm) ?? utm
  const host = ft.referrer_domain?.trim().toLowerCase()
  // a room invite is a door someone was handed, not an app page they bounced through
  if (ft.landing_path?.startsWith("/ours/join/")) return "invite"
  if (!host || isOwnHost(host, ownHosts) || AUTH_RETURN.has(host)) {
    // "direct" is a claim: they typed the address, or a link stripped the
    // referrer on the way in. A record whose first page is inside the app was
    // never a landing at all — it is the sign-in hop describing itself, and
    // the honest word for where that person came from is that we don't know.
    return ft.landing_path && APP_LANDING.test(ft.landing_path) ? "unknown" : "direct"
  }
  return channelOfHost(host) ?? "direct"
}

/**
 * Fold a list of `came_from` counts (utm_source, else host, else 'direct') into
 * channels, summing what used to be split: `reddit.com` + `com.reddit.frontpage`
 * + `old.reddit.com` become one `reddit` row. Largest first.
 */
export function foldChannels<T extends { name: string; n: number; n_30d?: number }>(
  counts: readonly T[],
  ownHosts: readonly string[] = ["innernet.live"],
): { name: string; n: number; n_30d?: number }[] {
  const out = new Map<string, { name: string; n: number; n_30d?: number }>()
  for (const c of counts) {
    const raw = (c.name ?? "").trim().toLowerCase()
    // a came_from of our own host or the google login only ever comes from the
    // sign-in hop's record, never from a landing — it is "unknown", not "direct"
    const name =
      !raw || raw === "direct"
        ? "direct"
        : isOwnHost(raw, ownHosts) || AUTH_RETURN.has(raw)
          ? "unknown"
          : UTM_ALIASES[raw] ?? channelOf({ referrer_domain: raw }, ownHosts)
    const row = out.get(name) ?? { name, n: 0 }
    row.n += c.n
    if (typeof c.n_30d === "number") row.n_30d = (row.n_30d ?? 0) + c.n_30d
    out.set(name, row)
  }
  return [...out.values()].sort((a, b) => b.n - a.n)
}

/**
 * A landing path that is safe to copy somewhere else. An invite path carries the
 * invite's token (`/ours/join/<token>`), which is a credential for that room:
 * it stays in our own table and never travels to an analytics vendor.
 */
export function shareablePath(path: string | null | undefined): string | undefined {
  if (!path) return undefined
  return path.replace(/^\/ours\/join\/[^/?#]+/, "/ours/join/:token")
}
